// Where the ask came IN and where the answer goes OUT — the two closed
// vocabularies an envelope is addressed by. See `../task-envelope.ts` for the
// file docblock.

import { z } from 'zod';

export const TaskOrigin = {
  Conversation: 'conversation',
  GhIssue: 'gh_issue',
  Sentry: 'sentry',
  Linear: 'linear',
  Manual: 'manual',
  /**
   * A PR-review trigger (slice 06, doc 06 "AI Review"): a `pull_request`
   * webhook cleared the review gate (`@developerz/review` `planReview`) and the
   * reviewer runs as a fleet task on the user's box. Carries a {@link ReviewTaskSpec}
   * in `review`; the runner switches on this origin instead of the coding adapter.
   */
  PrReview: 'pr_review',
  /**
   * A PR-review-THREAD trigger (#258): a human replied on one of our review
   * threads (`issue_comment` / `pull_request_review_comment` webhook cleared
   * the {@link ReviewReplyTaskSpec} gate). The runner fetches the PR's unresolved
   * review threads, engages the human ones (a grounded reply on the account's
   * own model), resolves what's addressed, and stores the exchange as KB for
   * future reviews. Carries a {@link ReviewReplyTaskSpec} in `review_reply`.
   */
  PrReviewReply: 'pr_review_reply',
  /**
   * A PR-BABYSIT trigger (#543, epic #541): a fleet-opened PR needs a sweep —
   * fix red CI, address human comments / our reviewer findings, or reply to a
   * bot — to drive it toward a merge (doc 04). Carries a {@link BabysitTaskSpec}
   * in `babysit`. Schema-only for now (this slice writes the PR-state projection
   * that FEEDS the babysit decision; the dispatch that mints this origin lands in
   * a later slice — nothing produces it yet).
   */
  PrBabysit: 'pr_babysit',
  /**
   * A PR-BABYSIT WATCH anchor (#843): the babysit driver covers non-fleet PRs
   * (Dependabot / external) that carry no `dz-task` marker and so have no
   * fleet-opened owning task. This origin marks the synthetic, born-`completed`
   * task that holds the deterministic watch state for such a PR — the analog of
   * a fleet task's own row. It never runs a coding loop (it's created terminal,
   * so the assigner never claims it); the driver advances its status through the
   * babysit states and mints ordinary `pr_babysit` follow-ups for any fix work.
   * Kept distinct from `pr_babysit` so it stays out of the follow-up bookkeeping.
   */
  PrBabysitWatch: 'pr_babysit_watch',
  /**
   * An INTERACTIVE SCOUT session (arc 101 slice 07, doc 07 "MCP scout entry"): a raw
   * ask against a repo that a mailbox-capable box turns into a plan, co-devised with a
   * human over the live mailbox. Minted through the normal dispatcher with scheduling
   * `class: 'interactive'` — the `plan_start` MCP verb is its producer
   * (`apps/api/src/deps/plan-start.ts`). Distinct
   * from a scout-AUTHORED execution task (which carries `origin: 'manual'` +
   * `dispatched_by.source: 'scout'`): THIS marks the scout SESSION itself, the signal
   * a `universal` box reads (`isInteractiveAssignment`, `apps/runner`) to hold the
   * mailbox and run the scout executor. An INTERACTIVE origin — never coding work.
   */
  Scout: 'scout',
  /**
   * An INTERACTIVE SETUP session (arc 101 slice 07): the conversational bootstrap role
   * (#313) dispatched as an assignment — establish a repo's base (CLAUDE.md, `bin/`,
   * harness, CodeGraph) with a human steering over the mailbox. Same interactive
   * posture as {@link TaskOrigin.Scout}: mailbox-holding, run by the setup executor on
   * a universal box, never the coding adapter.
   */
  Setup: 'setup',
  /**
   * A DEVOPS / DEBUGGER run (arc 101 slice 06, `docs/idea/roles.md` § the missing
   * role): reproduce a failure against the repo's own secrets, diagnose the running
   * system, act on infrastructure under the existing `ops_action` approval gate, and
   * — when the fix is bigger than a debugging session — FILE a raw task instead of
   * half-doing the work.
   *
   * **AUTONOMOUS, not interactive.** It reports and decides; it holds no mailbox, so
   * it routes through `routeAutonomous` (`apps/runner/src/modes/router.ts`) beside
   * `pr_review` / `pr_babysit`, never through {@link TaskOrigin.Scout}'s interactive
   * table. The doctor contract applies to it like every other post-planning role.
   *
   * It gains NO capability of its own on the box. Its outcome is the existing
   * `report` / `ops_action` pair (`packages/domain/src/entities/task.ts` `OutcomeKind`),
   * and a production `ops_action` still refuses without a fresh signed `OpsApproval` —
   * this origin adds a LANE and a BRIEF, never a second path past that gate.
   */
  Devops: 'devops',
  /**
   * A MODEL CALL OFFERED TO A MACHINE (#6759, epic #6552): the triage judge's one
   * structured call over a batch of asks, run on an org machine instead of in the
   * cluster. Carries a {@link ModelCallTaskSpec} in `model_call` (the `model_call_jobs`
   * row the prompt waits in), and `repo_full_name` is the batch's anchor repo. No
   * checkout, no diff: it delivers a result into run events. The claim requires
   * `harness:model-call`, which only the platform derives
   * (`requiredCapabilitiesForOrigin`) for a build that answers one
   * (`RUNNER_MODEL_CALL_MIN_VERSION`, #6760), so an older box is never handed one.
   *
   * Reader-first: nothing mints it in this release; #6762 is the first caller.
   */
  TriageJudge: 'triage_judge',
  /**
   * The KB gardener's FOLD call (#6759), offered to a machine the way
   * {@link TaskOrigin.TriageJudge} is; `repo_full_name` is the folded repo. Same
   * capability, same posture. Reader-first: #6763 is the first caller.
   */
  KbFold: 'kb_fold',
  /**
   * The PLANNER's plan call (#7569, epic #6552, plan 101 `02-planner-origin.md`), offered
   * to a machine the way {@link TaskOrigin.TriageJudge} is: the scout hands a doable ask
   * off (`scout_doable`), and the Planner (`AgentId.Planner`) authors its plan in one
   * model call. Same `model_call` spec and posture, but the claim ALSO requires
   * `harness:planner` (`requiredCapabilitiesForOrigin`), because a plan call's schema key
   * is learned only from a build that answers it.
   *
   * Reader-first: nothing mints it in this release; #6764 is the first caller, and no
   * build derives `harness:planner` until #6765.
   */
  PlanAuthor: 'plan_author',
} as const;
export const TaskOriginSchema = z.enum(TaskOrigin);
export type TaskOrigin = z.infer<typeof TaskOriginSchema>;

export const TaskSink = {
  /** External MCP `create_issue` on the maintainer's Linear (their creds). */
  Linear: 'linear',
  /** Opens a GitHub issue via `IssueActions.createFromInput`. */
  Gh: 'gh',
  /** Enqueues a coding task (→ BYOVM pull queue) — the terse single-shot enqueue. */
  Direct: 'direct',
  /**
   * The capacity-aware fan-out sink (the north-star "one ask → many tasks across
   * repos" path). Same durable `pending` enqueue as {@link TaskSink.Direct}, but
   * it (a) enforces the org's max queue depth (backpressure — a runaway fan-out
   * is refused as `queue_full`, never silently piling on) and (b) reports the
   * task's `queue_position` + the org's `queue_depth` so the caller can watch the
   * backlog drain as runner capacity frees. Concurrency itself stays bounded by
   * the seat-cap gate at claim time (`@developerz/billing` `entitlements`) — this
   * sink only bounds how many may WAIT, not how many run.
   */
  Queued: 'queued',
} as const;
export const TaskSinkSchema = z.enum(TaskSink);
export type TaskSink = z.infer<typeof TaskSinkSchema>;
